import { c as _c } from "react/compiler-runtime";
// `<OgFundingBar>` — single-fill horizontal progress bar for funding /
// goal-style values. Used by the proposal OG card to show
// `totalFunded / creationThreshold`.
//
// Mirrors the in-app `ProposalProgressBar` colour logic at OG-card
// scale: brand-blue while in progress, green when funded, amber when
// funded but spread is broken (`needs_debate`), muted when terminal.
//
// Renders the track + fill plus a row of labels beneath (left = value,
// right = percent / status). Caller passes `tone` rather than computing
// colour inline so route handlers stay declarative.
//
// Satori-friendly: single root, explicit `display: 'flex'`, inline
// styles only. The fill is absolutely-positioned inside the track —
// supported when both axes are explicit.

import type { CSSProperties } from 'react';
import { OG_CONTENT_WIDTH, OG_TOKENS } from '../tokens';
export type OgFundingBarTone = 'progress' | 'funded' | 'warning' | 'muted';
export interface OgFundingBarProps {
  /** Funded fraction in `[0, ∞)`. Visually clamped to `1` for the fill,
   *  but the caller can show ">100%" in the right label. */
  fraction: number;
  /** Tone selects the fill colour. See table below. */
  tone: OgFundingBarTone;
  /** Total bar width in pixels. Default `OG_CONTENT_WIDTH` (matches `OutcomeRow`). */
  width?: number;
  /** Bar height in pixels. Default 16. */
  height?: number;
  /** Left label under the bar (e.g. `"$1.6K / $2.5K"`). */
  leftLabel?: string;
  /** Right label under the bar (e.g. `"64% funded"` or `"Funded ✓"`). */
  rightLabel?: string;
}
const TRACK_BG: string = OG_TOKENS.gray800;
function fillColor(tone: OgFundingBarTone): string {
  switch (tone) {
    case 'funded':
      return OG_TOKENS.success500;
    case 'warning':
      return OG_TOKENS.warning500;
    case 'muted':
      return OG_TOKENS.gray500;
    case 'progress':
      return OG_TOKENS.brand500;
  }
}
function fillOpacity(tone: OgFundingBarTone): number {
  return tone === 'muted' ? 0.4 : 0.95;
}
export function OgFundingBar(props) {
  const $ = _c(30);
  const width = props.width ?? OG_CONTENT_WIDTH;
  const height = props.height ?? 16;
  const clamped = Math.max(0, Math.min(1, props.fraction));
  const fillWidth = clamped * width;
  const t0 = height / 2;
  let t1;
  if ($[0] !== height || $[1] !== t0 || $[2] !== width) {
    t1 = {
      display: "flex",
      position: "relative",
      width,
      height,
      background: TRACK_BG,
      borderRadius: t0,
      overflow: "hidden"
    };
    $[0] = height;
    $[1] = t0;
    $[2] = width;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const trackStyle = t1;
  let t2;
  if ($[4] !== props.tone) {
    t2 = fillColor(props.tone);
    $[4] = props.tone;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== props.tone) {
    t3 = fillOpacity(props.tone);
    $[6] = props.tone;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = height / 2;
  let t5;
  if ($[8] !== fillWidth || $[9] !== height || $[10] !== t2 || $[11] !== t3 || $[12] !== t4) {
    t5 = {
      display: "flex",
      position: "absolute",
      left: 0,
      top: 0,
      width: fillWidth,
      height,
      background: t2,
      opacity: t3,
      borderRadius: t4
    };
    $[8] = fillWidth;
    $[9] = height;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const fillStyle = t5;
  let t6;
  if ($[14] !== width) {
    t6 = {
      display: "flex",
      width,
      justifyContent: "space-between",
      alignItems: "center",
      fontSize: 22,
      fontWeight: 500,
      color: OG_TOKENS.gray100,
      marginTop: 10
    };
    $[14] = width;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const labelRowStyle = t6;
  const showLabels = props.leftLabel !== undefined || props.rightLabel !== undefined;
  let t7;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      display: "flex",
      flexDirection: "column"
    };
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== fillStyle) {
    t8 = <div style={fillStyle} />;
    $[17] = fillStyle;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t8 || $[20] !== trackStyle) {
    t9 = <div style={trackStyle}>{t8}</div>;
    $[19] = t8;
    $[20] = trackStyle;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== labelRowStyle || $[23] !== props.leftLabel || $[24] !== props.rightLabel || $[25] !== showLabels) {
    t10 = showLabels && <div style={labelRowStyle}><span style={{
        display: "flex",
        opacity: 0.85
      }}>{props.leftLabel ?? ""}</span><span style={{
        display: "flex"
      }}>{props.rightLabel ?? ""}</span></div>;
    $[22] = labelRowStyle;
    $[23] = props.leftLabel;
    $[24] = props.rightLabel;
    $[25] = showLabels;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t10 || $[28] !== t9) {
    t11 = <div style={t7}>{t9}{t10}</div>;
    $[27] = t10;
    $[28] = t9;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  return t11;
}